import React, { useEffect, useState, Component } from 'react';
import { motion } from 'framer-motion';
interface WeatherData {
  temp: string;
  dew: string;
  humidity: string;
  windSpeed: string;
  windGust: string;
  windDir: string;
  civilDawn: string;
  civilDusk: string;
}
const EMPTY_DATA: WeatherData = {
  temp: '—',
  dew: '—',
  humidity: '—',
  windSpeed: '—',
  windGust: '—',
  windDir: '—',
  civilDawn: '—',
  civilDusk: '—'
};
export function Meteo() {
  const [data, setData] = useState<WeatherData>(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('[METEO] Fetching weather data...');
        const response = await fetch('https://api.allorigins.win/raw?url=' + encodeURIComponent('http://93.99.194.142:2358/xml.xml'));
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const text = await response.text();
        console.log('[METEO] Raw XML response:', text.substring(0, 500));
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(text, 'text/xml');
        const parseError = xmlDoc.querySelector('parsererror');
        if (parseError) {
          console.error('[METEO] XML parse error:', parseError.textContent);
          // Try parsing as HTML instead (proxy might wrap in HTML)
          const htmlDoc = parser.parseFromString(text, 'text/html');
          const body = htmlDoc.body?.textContent?.trim();
          if (body) {
            console.log('[METEO] Attempting HTML fallback parse...');
            const xmlDoc2 = parser.parseFromString(body, 'text/xml');
            if (!xmlDoc2.querySelector('parsererror')) {
              processXml(xmlDoc2);
              return;
            }
          }
          throw new Error('XML parse error');
        }
        processXml(xmlDoc);
      } catch (err: any) {
        console.error('[METEO] Failed to fetch/parse weather data:', err);
        setError(err?.message || 'Chyba načítání');
        setLoading(false);
      }
    };
    const processXml = (xmlDoc: Document) => {
      // The XML structure is:
      // <wario degree="C" pressure="hPa" date="..." time="..." ...>
      //   <input>
      //     <sensor>
      //       <type>temperature</type>
      //       <id>1006</id>
      //       <name>Temperature</name>
      //       <place/>
      //       <value>4.3</value>
      //     </sensor>
      //     ...
      //   </input>
      //   <output>
      //     <civstart>HH:MM</civstart>
      //     <civend>HH:MM</civend>
      //     ...
      //   </output>
      // </wario>
      // Log root element info
      const root = xmlDoc.documentElement;
      console.log('[METEO] Root element:', root?.tagName, 'attributes:', Array.from(root?.attributes || []).map(a => `${a.name}="${a.value}"`).join(', '));
      // Find all <sensor> elements and build a map by <id> child text
      const sensors = xmlDoc.getElementsByTagName('sensor');
      const sensorMap: Record<string, string> = {};
      console.log('[METEO] Found', sensors.length, 'sensor elements');
      for (let i = 0; i < sensors.length; i++) {
        const sensor = sensors[i];
        const idEl = sensor.getElementsByTagName('id')[0];
        const valueEl = sensor.getElementsByTagName('value')[0];
        const typeEl = sensor.getElementsByTagName('type')[0];
        const nameEl = sensor.getElementsByTagName('name')[0];
        const id = idEl?.textContent?.trim() || '';
        const value = valueEl?.textContent?.trim() || '';
        const type = typeEl?.textContent?.trim() || '';
        const name = nameEl?.textContent?.trim() || '';
        if (id) {
          sensorMap[id] = value;
          console.log(`[METEO] Sensor id=${id} type=${type} name=${name} value=${value}`);
        }
      }
      // Look up a value by sensor ID from the map
      const getById = (id: string): string | null => {
        return sensorMap[id] || null;
      };
      // Look up a value by tag name (e.g. <civstart>05:23</civstart>)
      const getByTag = (name: string): string | null => {
        const els = xmlDoc.getElementsByTagName(name);
        if (els.length > 0) {
          const text = els[0].textContent?.trim();
          if (text) return text;
          const val = els[0].getAttribute('value') || els[0].getAttribute('val');
          if (val) return val;
        }
        return null;
      };
      // Detect wind unit from root element or sensor data
      // The wario station typically reports wind in km/h
      const rootDegree = root?.getAttribute('degree') || '';
      const rootPressure = root?.getAttribute('pressure') || '';
      console.log('[METEO] Units - degree:', rootDegree, 'pressure:', rootPressure);
      // Check for explicit wind unit in XML
      const windUnitRaw = getByTag('windunit') || getByTag('wunit') || getByTag('windunits') || getByTag('speedunit') || '';
      const windUnit = windUnitRaw.toLowerCase();
      console.log('[METEO] Detected wind unit:', windUnit || '(none - assuming km/h)');
      const convertToKnots = (val: string | null): string => {
        if (!val) return '—';
        const num = parseFloat(val.replace(',', '.'));
        if (isNaN(num)) return val;
        let knots: number;
        if (windUnit.includes('km') || windUnit.includes('kmh') || windUnit.includes('kph')) {
          knots = num / 1.852;
        } else if (windUnit.includes('mph') || windUnit.includes('mi')) {
          knots = num / 1.151;
        } else if (windUnit.includes('m/s') || windUnit.includes('ms') || windUnit.includes('mps')) {
          knots = num * 1.944;
        } else {
          // Default: assume km/h
          knots = num / 1.852;
        }
        return Math.round(knots).toString();
      };
      const formatTemp = (val: string | null): string => {
        if (!val) return '—';
        const num = parseFloat(val.replace(',', '.'));
        if (isNaN(num)) return val;
        return Math.round(num).toString();
      };
      const formatNum = (val: string | null): string => {
        if (!val) return '—';
        const num = parseFloat(val.replace(',', '.'));
        if (isNaN(num)) return val;
        return Math.round(num).toString();
      };
      // Format time "H:M" or "HH:MM" → "HH:MM CET"
      const formatTime = (val: string | null): string => {
        if (!val || val === '—') return '—';
        const parts = val.split(':');
        if (parts.length !== 2) return val;
        const h = parts[0].trim().padStart(2, '0');
        const m = parts[1].trim().padStart(2, '0');
        return `${h}:${m} CET`;
      };
      // Use specific IDs from the XML feed
      const weatherData: WeatherData = {
        temp: formatTemp(getById('1006')),
        dew: formatTemp(getById('1010')),
        humidity: formatNum(getById('1007')),
        windSpeed: convertToKnots(getById('1003')),
        windGust: convertToKnots(getById('1004')),
        windDir: formatNum(getById('1002')),
        civilDawn: formatTime(getByTag('civstart')),
        civilDusk: formatTime(getByTag('civend'))
      };
      console.log('[METEO] Parsed weather data:', weatherData);
      setData(weatherData);
      setError(null);
      setLoading(false);
    };
    fetchData();
  }, []);
  // ALWAYS render the section — show loading/error states inline
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.8,
    delay: 0.5
  }} className="w-full max-w-7xl mx-auto px-6 mb-12 relative z-20" data-id="element-140">
      <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8 lg:gap-12" data-id="element-141">
        {/* Label */}
        <div className="hidden lg:block shrink-0" data-id="element-142">
          <h3 className="text-[#38bdf8] font-thin text-2xl tracking-[0.2em] uppercase border-l-2 border-[#38bdf8] pl-4" data-id="element-143">
            Meteo
          </h3>
        </div>

        {/* Data Grid */}
        <div className="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-8 lg:gap-4" data-id="element-144">
          <WeatherItem label="Teplota" value={data.temp} unit="°C" loading={loading} data-id="element-145" />
          <WeatherItem label="Rosný bod" value={data.dew} unit="°C" loading={loading} data-id="element-146" />
          <WeatherItem label="Vlhkost" value={data.humidity} unit="%" loading={loading} data-id="element-147" />
          <WeatherItem label="Vítr" value={data.windSpeed} unit="kt" loading={loading} data-id="element-148" />
          <WeatherItem label="Nárazy" value={data.windGust} unit="kt" loading={loading} data-id="element-149" />
          <WeatherItem label="Směr" value={data.windDir} unit="°" loading={loading} data-id="element-150" />

          {/* Dawn/Dusk Group */}
          <div className="col-span-2 flex flex-col justify-center gap-1.5 border-t sm:border-t-0 sm:border-l border-white/10 pt-4 sm:pt-0 sm:pl-4 mt-2 sm:mt-0" data-id="element-151">
            <div className="flex justify-between items-baseline gap-2 text-xs text-white/40" data-id="element-152">
              <span className="uppercase tracking-wider whitespace-nowrap" data-id="element-153">
                Úsvit
              </span>
              <span className={`font-mono text-sm whitespace-nowrap ${loading ? 'text-[#38bdf8]/30 animate-pulse' : 'text-[#38bdf8]'}`} data-id="element-154">
                {data.civilDawn}
              </span>
            </div>
            <div className="flex justify-between items-baseline gap-2 text-xs text-white/40" data-id="element-155">
              <span className="uppercase tracking-wider whitespace-nowrap" data-id="element-156">
                Soumrak
              </span>
              <span className={`font-mono text-sm whitespace-nowrap ${loading ? 'text-[#38bdf8]/30 animate-pulse' : 'text-[#38bdf8]'}`} data-id="element-157">
                {data.civilDusk}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Error indicator — subtle, doesn't break layout */}
      {error && <p className="text-white/20 text-[10px] text-center mt-4 font-mono" data-id="element-158">
          meteo nedostupné ({error})
        </p>}
    </motion.div>;
}
function WeatherItem({
  label,
  value,
  unit,
  loading
}: {
  label: string;
  value: string;
  unit: string;
  loading?: boolean;
}) {
  return <div className="flex flex-col" data-id="element-159">
      <span className="text-[10px] uppercase tracking-widest text-white/40 mb-1" data-id="element-160">
        {label}
      </span>
      <div className="flex items-baseline" data-id="element-161">
        <span className={`text-3xl font-extralight tracking-tighter ${loading ? 'text-[#38bdf8]/30 animate-pulse' : 'text-[#38bdf8]'}`} data-id="element-162">
          {value}
        </span>
        <span className="ml-1 text-sm text-[#38bdf8]/50 font-light" data-id="element-163">
          {unit}
        </span>
      </div>
    </div>;
}